import { Box, Grid, Paper, Stack, Typography } from "@mui/material"
import React, { FC, useCallback, useState } from "react"
import { useTranslation } from "react-i18next"
import {
  ActionReqExecutionModeEnum,
  ActionReqThingTypeMrn,
  ActionReqTypeEnum,
} from "shared/actionReqData"
import { Locker } from "shared/locker"
import { VirtualLockerActionEnum } from "shared/lockerData"
import type { Sst2Door, Sst2Locker } from "shared/sst2"
import { useCreateThingsMutation } from "src/features/things/thingsApi"
import { useCurrentTenant } from "src/hooks/useCurrentTenant"
import { v4 as uuidv4 } from "uuid"
import { InteractiveDoor } from "./InteractiveDoor"
import { LockerControllerActions } from "./LockerControllerActions"
import { LockerNumericPad } from "./LockerNumericPad"
import { OsLockerDoorV2 } from "./OsLockerDoorV2"

type LockerControllerV2Props = {
  locker: Locker
  onDoorClick?: (door: Sst2Door) => void
}

export const LockerControllerV2: FC<LockerControllerV2Props> = React.memo(
  ({ locker, onDoorClick: externalDoorClick }) => {
    const { t } = useTranslation()
    const [createThings] = useCreateThingsMutation()
    const { tenant } = useCurrentTenant()
    const [highlightedDial, setHighlightedDial] = useState("")
    const [inputCode, setInputCode] = useState("")
    const [selectedDoorId, setSelectedDoorId] = useState<string | null>(null)

    const sst2Locker = locker.sst2Locker as Sst2Locker | undefined
    const doors = sst2Locker?.doors || []

    const onDialPress = useCallback((value: string) => {
      setHighlightedDial(value)
    }, [])

    const onSubmitCode = useCallback(async () => {
      if (!tenant) {
        console.error("No tenant")
        return
      }
      try {
        const actionReq = {
          thingTypeMrn: ActionReqThingTypeMrn,
          tenantId: tenant.id,
          idempotencyKey: uuidv4(),
          actionType: ActionReqTypeEnum.TriggerVirtualLockerAction,
          executionMode: ActionReqExecutionModeEnum.sync,
          inputData: {
            lockerId: locker.id,
            lockerAction: VirtualLockerActionEnum.SubmitCode,
            code: inputCode,
          },
        }
        await createThings({
          body: { things: [actionReq] },
          params: { tenantId: tenant.id },
        }).unwrap()
      } catch (err) {
        console.error("Error submitting code", err)
      }
    }, [createThings, inputCode, locker.id, tenant])

    const onEnter = useCallback(() => {
      onSubmitCode()
      setHighlightedDial("")
    }, [onSubmitCode])

    const onDoorClick = useCallback(
      async (door: Sst2Door) => {
        setSelectedDoorId(door.id)

        if (externalDoorClick) {
          externalDoorClick(door)
          return
        }

        // Default: close door via virtual locker action
        if (!tenant) {
          console.error("No tenant")
          return
        }
        try {
          const actionReq = {
            thingTypeMrn: ActionReqThingTypeMrn,
            tenantId: tenant.id,
            idempotencyKey: uuidv4(),
            actionType: ActionReqTypeEnum.TriggerVirtualLockerAction,
            executionMode: ActionReqExecutionModeEnum.sync,
            inputData: {
              lockerId: locker.id,
              lockerAction: VirtualLockerActionEnum.CloseDoor,
              sstDoorId: door.id,
            },
          }
          await createThings({
            body: { things: [actionReq] },
            params: { tenantId: tenant.id },
          }).unwrap()
        } catch (err) {
          console.error("Error closing door", err)
        }
      },
      [createThings, externalDoorClick, locker.id, tenant],
    )

    return (
      <Stack direction="column" spacing={1} sx={{ px: 4, pt: 4 }}>
        <LockerControllerActions locker={locker} />

        <Stack
          direction="row"
          spacing={2}
          alignItems="flex-start"
        >
          <Stack direction="column">
            <Paper variant="outlined" sx={{ p: 2 }}>
              <LockerNumericPad
                onPress={onDialPress}
                onEnter={onEnter}
                highlightedDial={highlightedDial}
                lockerId={locker.id}
                onChange={setInputCode}
              />
            </Paper>
          </Stack>

          <Paper
            variant="outlined"
            sx={{
              p: 2,
              bgcolor: "rgba(0, 0, 0, 0.02)",
              flex: 1,
              maxWidth: 1000,
            }}
          >
            <Typography variant="body2" fontWeight="bold" sx={{ mb: 1.5 }}>
              {`${t("doors")} (${doors.length})`}
            </Typography>
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 1.5,
              }}
            >
              {doors.map((door) => (
                <OsLockerDoorV2
                  key={door.id}
                  door={door}
                  isSelected={selectedDoorId === door.id}
                  onClick={() => onDoorClick(door)}
                />
              ))}
            </Box>
          </Paper>
        </Stack>
      </Stack>
    )
  },
)

LockerControllerV2.displayName = "LockerControllerV2"
